import { useCallback, useEffect, useMemo, useState } from "react";
import { GitBranch, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { hasAnyPermission } from "../lib/permissions";
import { PLAN_FEATURES, hasPlanFeature } from "../lib/planFeatures";
import { DataPanel, PageHeader } from "../components/CommandSystem";

const BRANCH_TYPES = [
  {
    type: "main",
    title: "Main Branch",
    defaultName: "Main Branch",
    description: "Sees and manages all branch data across Hoardexa.",
  },
  {
    type: "local",
    title: "Local Branch",
    defaultName: "Local Branch",
    description: "Works only with local branch clients, campaigns, accounts, and reports.",
  },
  {
    type: "agency",
    title: "Agency Branch",
    defaultName: "Agency Branch",
    description: "Works only with agency branch clients, campaigns, accounts, and reports.",
  },
];

function branchLabel(type) {
  return BRANCH_TYPES.find((item) => item.type === type)?.title || "Branch";
}

export function BranchSettings({ showHeader = true }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState("");
  const canManage = hasAnyPermission(user, ["invite_employees", "edit_employee_permissions"]);
  const canUseBranchSegregation = hasPlanFeature(user, PLAN_FEATURES.branches);

  const branchesByType = useMemo(() => {
    return Object.fromEntries((branches || []).map((branch) => [branch.branch_type, branch]));
  }, [branches]);

  const hydrateDrafts = useCallback((items) => {
    const next = {};
    BRANCH_TYPES.forEach((config) => {
      const branch = items.find((item) => item.branch_type === config.type);
      next[config.type] = branch?.name || config.defaultName;
    });
    setDrafts(next);
  }, []);

  const loadBranches = useCallback(() => {
    setLoading(true);
    api.get("/branches", { skipGlobalLoader: true })
      .then(({ data }) => {
        const items = data || [];
        setBranches(items);
        hydrateDrafts(items);
      })
      .catch(() => toast.error("Failed to load branches"))
      .finally(() => setLoading(false));
  }, [hydrateDrafts]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  async function saveBranch(type) {
    const config = BRANCH_TYPES.find((item) => item.type === type);
    const name = (drafts[type] || "").trim();
    if (!name) {
      toast.error(`${branchLabel(type)} name is required`);
      return;
    }

    setSavingType(type);
    try {
      const existing = branchesByType[type];
      const { data } = existing
        ? await api.put(`/branches/${existing.id}`, { name })
        : await api.post("/branches", { name, branch_type: type });

      setBranches((prev) => {
        const withoutType = prev.filter((item) => item.branch_type !== type);
        return [...withoutType, data].sort((a, b) => {
          const aIndex = BRANCH_TYPES.findIndex((item) => item.type === a.branch_type);
          const bIndex = BRANCH_TYPES.findIndex((item) => item.type === b.branch_type);
          return aIndex - bIndex;
        });
      });
      setDrafts((prev) => ({ ...prev, [type]: data?.name || name || config.defaultName }));
      toast.success(`${branchLabel(type)} saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save branch");
    } finally {
      setSavingType("");
    }
  }

  return (
    <div className="space-y-5">
      {showHeader && (
        <PageHeader
          eyebrow="Workspace setup"
          title="Branches"
          subtitle="Configure Main, Local, and Agency branches for data visibility and employee assignment."
        />
      )}

      <DataPanel title="Branch configuration" subtitle="Set a display name for each branch type and activate it for employee assignment.">
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
          {BRANCH_TYPES.filter((config) => config.type === "main" || canUseBranchSegregation).map((config) => {
            const existing = branchesByType[config.type];
            const saving = savingType === config.type;
            return (
              <div key={config.type} className="hx-card flex h-full flex-col p-5">
                <div className="flex min-h-[9rem] items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <GitBranch size={17} className="text-blue-600" />
                      <h2 className="font-semibold text-slate-900">{config.title}</h2>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{config.description}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${existing ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {existing ? "Active" : "Not created"}
                  </span>
                </div>

                <div className="mt-auto space-y-1.5">
                  <Label htmlFor={`branch-name-${config.type}`} className="text-xs font-semibold uppercase tracking-wide text-slate-500">Branch display name</Label>
                  <Input
                    id={`branch-name-${config.type}`}
                    name={`branch_name_${config.type}`}
                    value={drafts[config.type] || ""}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [config.type]: e.target.value }))}
                    disabled={loading || !canManage}
                    placeholder={config.defaultName}
                  />
                </div>

                <Button
                  type="button"
                  className="mt-4 w-full gap-2 bg-blue-600 text-white hover:bg-blue-700"
                  disabled={loading || !canManage || saving}
                  onClick={() => saveBranch(config.type)}
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {existing ? "Save Branch" : "Create Branch"}
                </Button>
              </div>
            );
          })}
        </div>
      </DataPanel>

      {!canManage && (
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-500">
          You can view branch settings, but only users with employee management permission can create or rename branches.
        </div>
      )}
    </div>
  );
}

export function Branches() {
  return (
    <Layout title="" subtitle="">
      <BranchSettings />
    </Layout>
  );
}
